import Header from '../../Header/Header';
import Footer from '../../Footer/Footer';
import { useHistory, useParams } from 'react-router-dom';

import communityImage from '../../../assets/images/community-image-1.jpg';
import React, { useEffect, useState } from 'react';
import { currentDate, frontendRoutes } from '../../../Constants/constants';
import { useDispatch, useSelector } from 'react-redux';
import {
  addPropertyId,
  addStaticToken,
  clearPropertyContext,
  getBuilderId,
  get_property_details,
  tourDate,
} from '../../../redux/action/action';
import { Button } from '../../Button/Button';
import { Form } from 'react-bootstrap';
import { Disclosure } from '../../Disclosure/Disclosure';
import { FormikProvider, FormikValues, useFormik } from 'formik';
import { enterPropertyValidation } from '../../../Constants/validations';
import { BuilderSettings } from '../../../redux/types/builders';
import { useTranslation } from 'react-i18next';
import { fpGetEvent } from '../../../utils/fingerprint';
import { getCustomerIdFromToken, getPropertyAccessNextPath } from '../../../utils';
import PropertyRecovery from '../PropertyRecovery/PropertyRecovery';
import { usePropertyResolution } from '../../../hooks/usePropertyResolution';
import {
  createPropertyError,
  getPropertyAvailabilityErrorCode,
} from '../../../utils/propertyRecovery';

export const EnterProperty = () => {
  const { t } = useTranslation();
  const params = useParams<{ builder_name: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const builderId = useSelector((state: any) => state.builder.builder_id);
  const propertyBuilder = useSelector((state: any) => state?.property?.property?.builder);
  const userToken = localStorage.getItem('user_token');
  const [nextPage, setNextPage] = useState(false);
  const {
    error: propertyError,
    isLoadingProperty,
    clearError,
    reportError,
    loadProperty,
    findOtherProperties,
  } = usePropertyResolution();
  const builderSettings: BuilderSettings = useSelector((state: any) => state.builder.settings);
  const userInfo = useSelector((state: any) => state.user.userInfo);

  const formik = useFormik<FormikValues>({
    validateOnChange: false,
    initialValues: {
      propertyId: '',
      privacyPolicy: false,
      customDisclosureActive: false,
      customDisclosure: false,
    },
    validationSchema: enterPropertyValidation,
    onSubmit: values => {
      return checkProperty(values.propertyId);
    },
  });

  useEffect(() => {
    if (builderId) {
      return;
    }

    localStorage.setItem('builder_name', params.builder_name);

    dispatch(addStaticToken());
    dispatch(getBuilderId());
  }, [builderId, dispatch, params.builder_name]);

  useEffect(() => {
    if (!nextPage) {
      return;
    }

    const isDemoMode = propertyBuilder?.toLowerCase() === 'demo mode';

    !builderSettings.geolocation_active || isDemoMode
      ? history.push(frontendRoutes.LockCodeScreen(params.builder_name).path)
      : history.push(frontendRoutes.shareLocation(params.builder_name).path);

    setNextPage(false);
  }, [history, nextPage, params.builder_name, propertyBuilder, builderSettings.geolocation_active]);

  const checkProperty = async (propertyId: string, isRetry = false) => {
    dispatch(clearPropertyContext());

    const property = await loadProperty({
      propertyId,
      customerId: userInfo?.customer_id,
      isRetry,
    });

    if (!property) {
      return;
    }

    const propertyName = property.marketingName || property.address;
    const unavailableCode = getPropertyAvailabilityErrorCode(property);

    if (unavailableCode) {
      reportError(
        createPropertyError({
          code: unavailableCode,
          message: `Property ${propertyId} is not currently available for self-touring`,
          status: 403,
        }),
        { propertyId, propertyName, customerId: userInfo?.customer_id, isRetry },
      );
      return;
    }

    dispatch(get_property_details({ id: Number(propertyId), ...property }));
    dispatch(addPropertyId(propertyId));

    fpGetEvent({
      linkedId: getCustomerIdFromToken(userToken),
    });

    const nextPath = getPropertyAccessNextPath(
      params.builder_name,
      builderSettings.geolocation_active,
      property?.builder,
    );

    if (!property.didCompleteSurveyForBuilder) {
      history.push(frontendRoutes.surveyQuestions(params.builder_name).path, { nextPath });
      return;
    }

    dispatch(tourDate({
      showDate: 'Today ' + currentDate,
      radioTime: 'now',
      customTime: new Date().toLocaleTimeString('en-GU', {
        hour: 'numeric',
        minute: 'numeric',
        hour12: true,
      }),
    }));
    setNextPage(true);
  };

  const returnToPropertyEntry = () => {
    clearError();
    setNextPage(false);
    formik.setFieldValue('propertyId', '');
  };

  if (propertyError) {
    return (
      <PropertyRecovery
        error={propertyError}
        propertyId={String(formik.values.propertyId)}
        isRetrying={isLoadingProperty}
        showTryAgain
        onBack={returnToPropertyEntry}
        onTryAgain={returnToPropertyEntry}
        onFindOtherProperties={findOtherProperties}
      />
    );
  }

  return (
    <section className="main-section community-screen d-flex  justify-content-center">
      <div className="box">
        <Header />
        <span
          onClick={() => {
            history.goBack();
          }}
          className="link-back icn icn-back-arrow"
        ></span>
        <div className="box-data d-flex flex-column position-relative">
          <div className="mt-auto mb-auto">
            <h1 className="title" style={{ margin: '0 0 95px 0' }}>
              {t('enter_property_id')}
            </h1>
            <figure className="text-center community-image">
              <img src={communityImage} alt={t('community')} />
            </figure>
          </div>
          <Form
            onSubmit={formik.handleSubmit}
            style={{ marginTop: '20px' }}
          >
            <FormikProvider value={formik}>
              <Form.Group controlId="propertyId">
                <Form.Control
                  type="text"
                  placeholder={t('property_id_placeholder')}
                  name="propertyId"
                  onChange={formik.handleChange}
                  value={formik.values.propertyId}
                />
                {formik.errors.propertyId ? (
                  <span className="invalid-feedback d-block">{formik.errors.propertyId}</span>
                ) : (
                  ''
                )}
              </Form.Group>
              <div className="box-footer mt-auto">
                <Disclosure builderSettings={builderSettings} formik={formik} />
                <Button
                  type="submit"
                  className={`button w-100`}
                  style={{ lineHeight: 1.4, padding: '10px' }}
                >
                  {t('continue')}
                </Button>
              </div>
            </FormikProvider>
          </Form>
        </div>
        <Footer />
      </div>
    </section>
  );
};
